import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_BTN_GHOST,
  ADMIN_BTN_PRIMARY,
  ADMIN_CARD,
  ADMIN_CARD_MUTED,
  ADMIN_SECTION_TITLE,
} from './adminTheme';
import {
  commitReadabilityPrefs,
  defaultReadabilityPrefs,
  loadReadabilityPrefs,
  resetReadabilityPrefs,
} from '../../utils/readabilityPrefs';

/**
 * @param {{
 *   id: string,
 *   label: string,
 *   hint: string,
 *   value: number,
 *   min: number,
 *   max: number,
 *   step: number,
 *   display: string,
 *   onChange: (n: number) => void,
 * }} props
 */
function SliderRow({ id, label, hint, value, min, max, step, display, onChange }) {
  return (
    <label className="block space-y-1.5" htmlFor={id}>
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-200">{label}</span>
        <span className="font-mono text-xs tabular-nums text-cyan-200">{display}</span>
      </span>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 w-full cursor-pointer accent-cyan-400"
      />
      <span className="block text-[11px] leading-snug text-slate-400">{hint}</span>
    </label>
  );
}

/**
 * Admin console — live readability / legibility controls for the whole app (this browser).
 */
export default function AdminReadabilityPanel() {
  const [prefs, setPrefs] = useState(() => loadReadabilityPrefs());
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    commitReadabilityPrefs(prefs);
  }, [prefs]);

  const patch = useCallback((partial) => {
    setPrefs((prev) => ({ ...prev, ...partial }));
    setSavedFlash(true);
  }, []);

  useEffect(() => {
    if (!savedFlash) return undefined;
    const t = window.setTimeout(() => setSavedFlash(false), 900);
    return () => window.clearTimeout(t);
  }, [savedFlash]);

  const onReset = useCallback(() => {
    const next = resetReadabilityPrefs();
    setPrefs(next);
    setSavedFlash(true);
  }, []);

  const isDefault = useMemo(() => {
    const d = defaultReadabilityPrefs();
    return (
      prefs.brightness === d.brightness &&
      prefs.contrast === d.contrast &&
      prefs.saturate === d.saturate &&
      prefs.textScale === d.textScale &&
      prefs.letterSpacing === d.letterSpacing &&
      prefs.fontSmoothing === d.fontSmoothing &&
      prefs.reduceMotion === d.reduceMotion
    );
  }, [prefs]);

  const pct = (n) => `${Math.round(n * 100)}%`;
  const em = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(3)}em`;

  return (
    <section className="space-y-4" data-testid="admin-readability-panel">
      <header className={`${ADMIN_CARD} space-y-2`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-black uppercase tracking-[0.14em] text-cyan-200">
              Readability &amp; display
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-300">
              Adjust brightness, contrast, and text scale for this browser. Changes apply live to
              Home, Admin, and legal pages. Stored locally (not shared with other devices).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {savedFlash ? (
              <span className="rounded-full border border-emerald-500/40 bg-emerald-950/50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-200">
                Applied
              </span>
            ) : null}
            <button
              type="button"
              className={ADMIN_BTN_GHOST}
              onClick={onReset}
              disabled={isDefault}
              data-testid="admin-readability-reset"
            >
              Reset defaults
            </button>
          </div>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className={`${ADMIN_CARD} space-y-5`}>
          <h3 className={ADMIN_SECTION_TITLE}>Tone &amp; clarity</h3>
          <SliderRow
            id="wiw-brightness"
            label="Brightness"
            hint="Raises or lowers overall luminance (dark UI often needs a slight bump)."
            value={prefs.brightness}
            min={0.7}
            max={1.45}
            step={0.01}
            display={pct(prefs.brightness)}
            onChange={(brightness) => patch({ brightness })}
          />
          <SliderRow
            id="wiw-contrast"
            label="Contrast"
            hint="Separates text from backgrounds; too high can look harsh."
            value={prefs.contrast}
            min={0.75}
            max={1.5}
            step={0.01}
            display={pct(prefs.contrast)}
            onChange={(contrast) => patch({ contrast })}
          />
          <SliderRow
            id="wiw-saturate"
            label="Saturation"
            hint="Accent color intensity (cyan / emerald badges)."
            value={prefs.saturate}
            min={0.6}
            max={1.4}
            step={0.01}
            display={pct(prefs.saturate)}
            onChange={(saturate) => patch({ saturate })}
          />
        </section>

        <section className={`${ADMIN_CARD} space-y-5`}>
          <h3 className={ADMIN_SECTION_TITLE}>Typography</h3>
          <SliderRow
            id="wiw-text-scale"
            label="Text scale"
            hint="Global font size multiplier (root rem). Helps tablet / laptop / phone equally."
            value={prefs.textScale}
            min={0.85}
            max={1.35}
            step={0.01}
            display={pct(prefs.textScale)}
            onChange={(textScale) => patch({ textScale })}
          />
          <SliderRow
            id="wiw-letter-spacing"
            label="Letter spacing"
            hint="Slight tracking can sharpen thin uppercase labels on dark glass."
            value={prefs.letterSpacing}
            min={-0.02}
            max={0.08}
            step={0.005}
            display={em(prefs.letterSpacing)}
            onChange={(letterSpacing) => patch({ letterSpacing })}
          />

          <fieldset className="space-y-2 border-0 p-0">
            <legend className="text-xs font-bold uppercase tracking-wider text-slate-200">
              Font smoothing
            </legend>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'auto', label: 'Auto (sharper on dark)' },
                { id: 'antialiased', label: 'Antialiased (softer)' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`${ADMIN_BTN_GHOST} ${
                    prefs.fontSmoothing === opt.id
                      ? 'border-cyan-400/50 bg-cyan-500/15 text-cyan-100'
                      : ''
                  }`}
                  aria-pressed={prefs.fontSmoothing === opt.id}
                  onClick={() => patch({ fontSmoothing: opt.id })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] leading-snug text-slate-400">
              On dark UIs, browser antialiasing can make light text look thin or fuzzy — prefer Auto
              if glyphs look muddy.
            </p>
          </fieldset>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-slate-950/50 px-3 py-2.5">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-cyan-400"
              checked={prefs.reduceMotion}
              onChange={(e) => patch({ reduceMotion: e.target.checked })}
              data-testid="admin-readability-reduce-motion"
            />
            <span>
              <span className="block text-xs font-bold uppercase tracking-wider text-slate-100">
                Prefer reduced motion
              </span>
              <span className="mt-0.5 block text-[11px] leading-snug text-slate-400">
                Softens decorative pulses / sweeps that can distract from reading results.
              </span>
            </span>
          </label>
        </section>
      </div>

      <section className={`${ADMIN_CARD_MUTED} space-y-3`} aria-label="Live preview sample">
        <h3 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
          Preview sample
        </h3>
        <p className="text-lg font-extrabold text-white">OSINT Core</p>
        <p className="text-sm font-semibold text-slate-200">
          Enterprise Digital Due Diligence &amp; Threat Assessment Workspace
        </p>
        <p className="text-xs leading-relaxed text-slate-300">
          Public-data only. Results inform risk decisions; they are not endorsements or legal
          findings.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-lg border border-emerald-400/50 bg-emerald-950/70 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-emerald-100">
            Format: Valid
          </span>
          <span className="rounded-lg border border-cyan-400/50 bg-cyan-950/50 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-cyan-100">
            High Relevance
          </span>
        </div>
        <button type="button" className={ADMIN_BTN_PRIMARY} disabled>
          Sample CTA
        </button>
      </section>
    </section>
  );
}
